import React, { ReactElement, useEffect, useState } from 'react';
import { HeadTag } from 'shared/components';
import { Box, CircularProgress, SvgIcon } from '@mui/material';
import NoAuditGatesCard from './components/noAuditGatesCard';
import AuditModal from './components/AuditGateModal';
import PageHeader from 'shared/components/PageHeader';
import { AppPrimaryButton } from 'shared/components/AppComponents';
import { ReactComponent as Plus } from 'assets/images/ic_plus.svg';
import { PoliciesService } from 'services';
import ApprovalGateCard from './components/ApprovalGateCard';
import { Toast } from 'shared/helpers';
import { useSelector } from 'react-redux';
import { IAppState } from 'state/RootReducer';

interface ApprovalGate {
  _id: string;
  gate_name: string;
  required_artifacts: string;
  reviewers: string;
  sort_order: number;
  organizationId: string;
  created_by: string;
  updated_by: string;
  deleted_by: string | null;
  deleted_at: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

const AuditReadiness = (): ReactElement => {
  const [showAddNew, setShowAddNew] = useState(false);
  const [approvalGates, setApprovalGates] = useState<ApprovalGate[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedGateId, setSelectedGateId] = useState<string | null>(null);
  const { orgACLConfig } = useSelector(
    (state: IAppState) => state.baseAppState
  );
  const getAllApprovals = (): void => {
    setLoading(true);
    PoliciesService.getAllApprovals()
      .then((response: any) => {
        setApprovalGates(response.data.data);
      })
      .catch((error) => {
        console.error('Failed to fetch dashboard data:', error);
        Toast.showErrorMessage(error?.response?.data?.msg);
      })
      .finally(() => {
        setLoading(false);
        setSelectedGateId(null);
      });
  };

  const handleCreateGate = (values: any): void => {
    setLoading(true);
    const payload = {
      gate_name: values.gatename,
      required_artifacts: values.gateartifact,
      reviewers: values.reviewers,
      sort_order: approvalGates.length + 1,
    };

    PoliciesService.addApprovalGate(payload)
      .then((response: any) => {
        if (response.data) {
          Toast.showSuccessMessage(response.msg);
          setShowAddNew(false);
          getAllApprovals();
        }
      })
      .catch((error) => {
        console.error('Failed to create approval gate:', error);
        Toast.showErrorMessage(error?.response?.data?.msg);
      })
      .finally(() => {
        setSelectedGateId(null);
        setLoading(false);
      });
  };

  const handleEditGate = (values: any): void => {
    if (!selectedGateId) return;
    setLoading(true);
    const payload = {
      gate_name: values.gatename,
      required_artifacts: values.gateartifact,
      reviewers: values.reviewers,
      // sort_order: values.sort_order,
    };

    PoliciesService.updateApprovalGate(selectedGateId, payload)
      .then((response) => {
        if (response.data) {
          Toast.showSuccessMessage(response.data.msg);
          setShowAddNew(false);
          getAllApprovals();
        }
      })
      .catch((error) => {
        console.error('Failed to update approval gate:', error);
        Toast.showErrorMessage(error.data.msg);
      })
      .finally(() => {
        setLoading(false);
        setSelectedGateId(null);
      });
  };

  const handleDeleteGate = (gateId: string): void => {
    setLoading(true);
    PoliciesService.deleteApprovalGate(gateId)
      .then(() => {
        getAllApprovals();
      })
      .catch((error) => {
        console.error('Failed to delete approval gate:', error);
        Toast.showErrorMessage(error?.response?.data?.msg);
      })
      .finally(() => {
        setLoading(false);
        setSelectedGateId(null);
      });
  };

  const handleEdit = (id: string): void => {
    setShowAddNew(true);
    setSelectedGateId(id);
  };

  const handleSubmit = (values: any): void => {
    if (selectedGateId) {
      handleEditGate(values);
    } else {
      handleCreateGate(values);
    }
  };

  const handleCloseModal = (): void => {
    setShowAddNew(false);
    setSelectedGateId(null);
  };

  useEffect(() => {
    getAllApprovals();
  }, []);

  return (
    <>
      <PageHeader
        title="AI Approval Gates"
        description="Configure mandatory gates that AI systems must pass through for approval"
        actions={
          <>
            {orgACLConfig.policies?.includes(
              'policies.approvalGates.create'
            ) && (
              <AppPrimaryButton
                onClick={() => setShowAddNew(true)}
                className="btn-small"
                title="Add Gate"
                startIcon={
                  <SvgIcon
                    component={Plus}
                    inheritViewBox
                    className="view-icon"
                    fontSize="inherit"
                    htmlColor="#76A81F"
                  />
                }
              />
            )}
          </>
        }
      />
      <Box className="policy-management audit-readiness">
        <HeadTag title="Approval Gates" />

        {loading && (
          <Box className="empty-content">
            <CircularProgress />
          </Box>
        )}

        {!loading && approvalGates.length === 0 ? (
          <Box className="audit-readiness-wrapper">
            <NoAuditGatesCard />
          </Box>
        ) : (
          !loading &&
          approvalGates.length > 0 &&
          approvalGates.map((approvalGate) => (
            <ApprovalGateCard
              key={approvalGate._id}
              approvalGate={approvalGate}
              onEdit={handleEdit}
              onDelete={handleDeleteGate}
            />
          ))
        )}

        <AuditModal
          open={showAddNew}
          selectedGateId={selectedGateId}
          onClose={handleCloseModal}
          onSubmit={handleSubmit}
        />
      </Box>
    </>
  );
};

export default AuditReadiness;
